import React from "react";
import { Button, Image, Select, Spin, Tag } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import DataTable from "../../../components/DataTable";
import PageHeader from "../../../components/PageHeader";
import { useTiendaPaquetes } from "../../../hooks/useTiendaPaquetes";
import { useTiendaColegios } from "../../../hooks/useTiendaColegios";
import {
  TiendaPaquete,
  NivelTienda,
} from "../../../services/tienda-paquetes/tienda-paquete.types";

const formatMXN = (pesos: number) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
  }).format(pesos);

const NIVEL_COLOR: Record<NivelTienda, string> = {
  PREESCOLAR: "purple",
  PRIMARIA: "blue",
};

const TiendaPaquetesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const schoolIdParam = searchParams.get("school_id");
  const schoolId = schoolIdParam ? Number(schoolIdParam) : undefined;

  const { tiendaColegios } = useTiendaColegios();
  const { tiendaPaquetes, loading, deleteTiendaPaquete } =
    useTiendaPaquetes(schoolId);

  const handleSchoolChange = (value: number | undefined) => {
    if (value) {
      setSearchParams({ school_id: String(value) });
    } else {
      setSearchParams({});
    }
  };

  const columns: ColumnsType<TiendaPaquete> = [
    {
      title: "Imagen",
      dataIndex: "imagen_url",
      key: "imagen_url",
      width: 70,
      render: (url: string | null) =>
        url ? (
          <Image
            src={url}
            width={40}
            height={40}
            style={{ objectFit: "cover" }}
          />
        ) : (
          <div
            style={{
              width: 40,
              height: 40,
              background: "var(--bo-inset-strong-bg)",
              borderRadius: 4,
            }}
          />
        ),
    },
    { title: "Título", dataIndex: "titulo", key: "titulo" },
    {
      title: "Nivel",
      dataIndex: "nivel",
      key: "nivel",
      render: (nivel: NivelTienda) => (
        <Tag color={NIVEL_COLOR[nivel]}>{nivel}</Tag>
      ),
    },
    { title: "Grado", dataIndex: "grado", key: "grado", width: 70 },
    {
      title: "Precio",
      dataIndex: "precio",
      key: "precio",
      render: (v: number) => formatMXN(v),
    },
    {
      title: "Activo",
      dataIndex: "activo",
      key: "activo",
      render: (v: boolean) => (
        <Tag color={v ? "green" : "red"}>{v ? "Sí" : "No"}</Tag>
      ),
    },
    {
      title: "Actualizado",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (v: string) => new Date(v).toLocaleDateString("es-MX"),
    },
  ];

  if (loading) return <Spin size="large" />;

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Tienda · Paquetes"
        extra={
          <Button
            type="primary"
            onClick={() => navigate("/tienda-paquetes/new")}
          >
            + Nuevo paquete
          </Button>
        }
      />
      <div style={{ marginBottom: 16 }}>
        <Select
          style={{ width: 280 }}
          placeholder="Filtrar por colegio"
          allowClear
          value={schoolId}
          onChange={handleSchoolChange}
          options={tiendaColegios.map((c) => ({
            value: c.id,
            label: c.nombre,
          }))}
        />
      </div>
      <DataTable
        data={tiendaPaquetes}
        columns={columns}
        onEdit={(record) =>
          navigate(`/tienda-paquetes/${record.id}`, { state: record.id })
        }
        onDelete={(record) => deleteTiendaPaquete(record.id)}
      />
    </div>
  );
};

export default TiendaPaquetesPage;
